import React from 'react';
import { MessageSquare } from 'lucide-react';

const getStatusColor = (status) => {
  switch (status) {
    case 'NEW': return 'bg-blue-100 text-blue-800';
    case 'READ': return 'bg-yellow-100 text-yellow-800';
    case 'RESPONDED': return 'bg-green-100 text-green-800';
    case 'CLOSED': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const ContactMessagesTab = ({ contact }) => {
  const {
    contactMessages, loadingContactMessages, contactStats, contactFilters, setContactFilters,
    selectedContactMessage, showContactDetails, closeContactDetails,
    fetchContactMessages, showReplyModal, setShowReplyModal, closeReplyModal, replyForm, setReplyForm,
    handleViewContactMessage, handleUpdateContactStatus, handleReplyToMessage,
  } = contact;

  const filteredMessages = contactMessages.filter((msg) => {
    if (contactFilters.status && msg.status !== contactFilters.status) return false;
    if (contactFilters.search) {
      const search = contactFilters.search.toLowerCase();
      return (
        msg.name.toLowerCase().includes(search) ||
        msg.email.toLowerCase().includes(search) ||
        msg.subject.toLowerCase().includes(search) ||
        msg.message.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Contact Messages</h2>
          <p className="text-sm text-gray-600">Manage customer inquiries and feedback</p>
        </div>
        <button onClick={fetchContactMessages} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          Refresh
        </button>
      </div>

      {/* Statistics */}
      {contactStats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-sm text-gray-600">Total Messages</p>
            <p className="text-2xl font-bold text-gray-900">{contactStats.total || 0}</p>
          </div>
          <div className="bg-blue-50 rounded-lg shadow-md p-4 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600">New</p>
            <p className="text-2xl font-bold text-blue-600">{contactStats.new || 0}</p>
          </div>
          <div className="bg-yellow-50 rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
            <p className="text-sm text-gray-600">Read</p>
            <p className="text-2xl font-bold text-yellow-600">{contactStats.read || 0}</p>
          </div>
          <div className="bg-green-50 rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <p className="text-sm text-gray-600">Responded</p>
            <p className="text-2xl font-bold text-green-600">{contactStats.responded || 0}</p>
          </div>
          <div className="bg-gray-50 rounded-lg shadow-md p-4 border-l-4 border-gray-500">
            <p className="text-sm text-gray-600">Closed</p>
            <p className="text-2xl font-bold text-gray-600">{contactStats.closed || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
            <select
              value={contactFilters.status}
              onChange={(e) => setContactFilters({ ...contactFilters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="NEW">New</option>
              <option value="READ">Read</option>
              <option value="RESPONDED">Responded</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={contactFilters.search}
              onChange={(e) => setContactFilters({ ...contactFilters, search: e.target.value })}
              placeholder="Search by name, email, subject, or message..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loadingContactMessages ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d44100] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading messages...</p>
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No contact messages found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMessages.map((message) => (
                <tr key={message.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{message.name}</div>
                        {message.phone && <div className="text-sm text-gray-500">{message.phone}</div>}
                      </div>
                      {message.status === 'NEW' && <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{message.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">{message.subject}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(message.status)}`}>
                      {message.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(message.createdAt).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleViewContactMessage(message.id)} className="text-[#d44100] hover:text-[#b83700] mr-3">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Contact Message Details Modal */}
      {showContactDetails && selectedContactMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Contact Message Details</h3>
                <button onClick={closeContactDetails} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Sender Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium">{selectedContactMessage.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{selectedContactMessage.email}</p>
                    </div>
                    {selectedContactMessage.phone && (
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium">{selectedContactMessage.phone}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-medium">{new Date(selectedContactMessage.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Subject</h4>
                  <p className="text-gray-900">{selectedContactMessage.subject}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Message</h4>
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedContactMessage.message}</p>
                </div>

                {selectedContactMessage.response && (
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Response</h4>
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedContactMessage.response}</p>
                    {selectedContactMessage.responseMethod && (
                      <p className="text-sm text-gray-600 mt-2">Replied via: {selectedContactMessage.responseMethod}</p>
                    )}
                    {selectedContactMessage.respondedAt && (
                      <p className="text-sm text-gray-600">On: {new Date(selectedContactMessage.respondedAt).toLocaleString()}</p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center space-x-3">
                    <select
                      value={selectedContactMessage.status}
                      onChange={(e) => handleUpdateContactStatus(selectedContactMessage.id, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    >
                      <option value="NEW">New</option>
                      <option value="READ">Read</option>
                      <option value="RESPONDED">Responded</option>
                      <option value="CLOSED">Closed</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    {selectedContactMessage.phone && (
                      <a
                        href={`https://wa.me/${selectedContactMessage.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        WhatsApp
                      </a>
                    )}
                    <a
                      href={`mailto:${selectedContactMessage.email}?subject=Re: ${selectedContactMessage.subject}`}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Email
                    </a>
                    <button onClick={() => setShowReplyModal(true)} className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]">
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedContactMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Reply to Message</h3>
              <form onSubmit={handleReplyToMessage}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Response Method</label>
                  <select
                    value={replyForm.responseMethod}
                    onChange={(e) => setReplyForm({ ...replyForm, responseMethod: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                  >
                    <option value="Email">Email</option>
                    {selectedContactMessage.phone && <option value="WhatsApp">WhatsApp</option>}
                    <option value="Phone">Phone</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Response Message *</label>
                  <textarea
                    required
                    value={replyForm.response}
                    onChange={(e) => setReplyForm({ ...replyForm, response: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
                    placeholder="Type your response here..."
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={closeReplyModal} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700]">
                    Send Reply
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactMessagesTab;
